import { z } from "zod/v4";

export namespace Calendar {
  export const dayOfWeekSchema = z.enum([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]);
  export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

  // Add overrides here if necessary
  export const asDayOfWeek = (date: Temporal.PlainDate): DayOfWeek => {
    switch (date.dayOfWeek) {
      case 1:
        return "Monday";
      case 2:
        return "Tuesday";
      case 3:
        return "Wednesday";
      case 4:
        return "Thursday";
      case 5:
        return "Friday";
      case 6:
        return "Saturday";
      case 7:
        return "Sunday";
      default:
        throw new Error(`Invariant violation: Invalid day of week: ${date.dayOfWeek}`);
    }
  };

  export const dateSelectorSchema = z.union([
    z.object({
      type: z.literal("default"),
    }),
    z.object({
      type: z.literal("dayOfWeek"),
      values: dayOfWeekSchema.array(),
    }),
  ]);

  export type DateSelector = z.infer<typeof dateSelectorSchema>;

  // Would be nice to attach this to a schema via check, but this is kind of designed to work with any type of object that has a list of date selectors
  export const validateDateSelectors = (
    items: readonly { dateSelector: DateSelector | null }[],
    addIssue: (issue: z.core.$ZodRawIssue) => void,
  ): void => {
    let defaultCount = 0;
    const dayOfWeekCounts = new Map<DayOfWeek, number>();
    for (const item of items) {
      if (!item.dateSelector) {
        continue;
      }
      if (item.dateSelector.type === "dayOfWeek") {
        for (const dayOfWeek of item.dateSelector.values) {
          dayOfWeekCounts.set(dayOfWeek, (dayOfWeekCounts.get(dayOfWeek) ?? 0) + 1);
        }
      } else if (item.dateSelector.type === "default") {
        defaultCount++;
      }
    }
    if (defaultCount !== 1) {
      addIssue({
        code: "custom",
        message: `Must have exactly one selector of type "default"`,
        input: items,
      });
    }
    const repeatedDaysOfWeek: DayOfWeek[] = [];
    for (const [dayOfWeek, count] of dayOfWeekCounts.entries()) {
      if (count > 1) {
        repeatedDaysOfWeek.push(dayOfWeek);
      }
    }
    if (repeatedDaysOfWeek.length > 0) {
      addIssue({
        code: "custom",
        message: `The following days of the week appeared in multiple selectors: ${repeatedDaysOfWeek.join(", ")}`,
        input: items,
      });
    }
  };
}
